import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import slugify from 'slugify';
import { 
  PlusCircle, 
  Edit, 
  Trash2, 
  Save,
  X,
  Eye,
  Pencil
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { renderMarkdown } from '../../lib/markdoc';
import { useToast } from '../../context/ToastContext';
import { Alert } from '../../components/Alert';
import type { BlogPost, BlogCategory } from '../../types/blog';

const blogPostSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required'),
  category_id: z.string().optional(),
  author_name: z.string().min(1, 'Author name is required'),
  status: z.enum(['draft', 'published', 'archived']),
  content: z.string(),
});

type BlogPostFormData = z.infer<typeof blogPostSchema>;

export function BlogManagement() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<BlogPostFormData>({
    resolver: zodResolver(blogPostSchema),
  });

  const content = watch('content');

  // Watch the title field to auto-generate slug
  const title = watch('title');
  useEffect(() => {
    if (title && !selectedPost) {
      const generatedSlug = slugify(title, {
        lower: true,
        strict: true,
        trim: true,
      });
      setValue('slug', generatedSlug);
    }
  }, [title, selectedPost, setValue]);

  useEffect(() => {
    if (selectedPost) {
      reset({
        title: selectedPost.title,
        slug: selectedPost.slug,
        category_id: selectedPost.category_id || '',
        author_name: selectedPost.author_name,
        status: selectedPost.status,
        content: selectedPost.content,
      });
    } else {
      reset({
        title: '',
        slug: '',
        category_id: '',
        author_name: '',
        status: 'draft',
        content: '',
      });
    }
  }, [selectedPost, reset]);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  async function fetchPosts() {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          category:blog_categories(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load blog posts');
    }
  }

  async function fetchCategories() {
    const { data } = await supabase
      .from('blog_categories')
      .select('*')
      .order('name');
    setCategories(data || []);
  }

  const onSubmit = async (data: BlogPostFormData) => {
    if (!user) {
      setError('You must be logged in to perform this action');
      return;
    }

    try {
      setError(null);

      const { data: post, error } = await supabase
        .from('blog_posts')
        .upsert({
          id: selectedPost?.id,
          ...data,
          user_id: user.id,
          published_at: data.status === 'published' ? new Date().toISOString() : null,
        })
        .select()
        .single();

      if (error) throw error;

      await fetchPosts();
      setSelectedPost(null);
      setIsEditing(false);
      showToast(selectedPost ? 'Blog post updated successfully' : 'Blog post created successfully', 'success');
    } catch (err) {
      console.error('Error saving post:', err);
      showToast('Failed to save blog post', 'error');
      setError('Failed to save blog post');
    }
  };

  const handleDelete = async (post: BlogPost) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', post.id);

      if (error) throw error;

      await fetchPosts();
      setSelectedPost(null);
      setIsEditing(false);
      showToast('Blog post deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting post:', err);
      showToast('Failed to delete blog post', 'error');
      setError('Failed to delete blog post');
    }
  };

  return (
    <div className="min-h-screen bg-[--bg-secondary] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-[--text-primary]">
            Blog Management
          </h1>
          <button
            onClick={() => {
              setSelectedPost(null);
              setIsEditing(true);
              setIsPreview(false);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            New Post
          </button>
        </div>

        {error && (
          <Alert type="error" className="mb-8">
            {error}
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Posts List */}
          <div className="bg-[--bg-primary] shadow rounded-lg overflow-hidden lg:col-span-1">
            <div className="p-6">
              <h2 className="text-lg font-medium text-[--text-primary] mb-4">
                Posts
              </h2>
              <div className="space-y-4">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="flex items-center justify-between p-4 bg-[--bg-secondary] rounded-lg"
                  >
                    <div>
                      <h3 className="text-sm font-medium text-[--text-primary]">
                        {post.title}
                      </h3>
                      <div className="mt-1 text-xs text-[--text-secondary]">
                        {format(new Date(post.created_at), 'MMM d, yyyy')} ·{' '}
                        {post.status}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedPost(post);
                          setIsEditing(true);
                          setIsPreview(false);
                        }}
                        className="p-2 text-[--text-disabled] hover:text-[--text-secondary]"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(post)}
                        className="p-2 text-error-400 hover:text-error-500"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Edit Form */}
          {isEditing && (
            <div className="bg-[--bg-primary] shadow rounded-lg overflow-hidden lg:col-span-2">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-medium text-[--text-primary]">
                    {selectedPost ? 'Edit Post' : 'New Post'}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedPost(null);
                        setIsEditing(false);
                      }}
                      className="p-2 text-[--text-disabled] hover:text-[--text-secondary]"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[--text-primary]">
                      Title
                    </label>
                    <input
                      type="text"
                      {...register('title')}
                      className="mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                    />
                    {errors.title && (
                      <p className="mt-2 text-sm text-error-600">{errors.title.message}</p>
                    )}
                  </div>

                  {selectedPost && (
                    <div>
                      <label className="block text-sm font-medium text-[--text-primary]">
                        Slug
                      </label>
                      <input
                        type="text"
                        {...register('slug')}
                        className="mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                      />
                      {errors.slug && (
                        <p className="mt-2 text-sm text-error-600">{errors.slug.message}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-[--text-primary]">
                      Author Name
                    </label>
                    <input
                      type="text"
                      {...register('author_name')}
                      className="mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                    />
                    {errors.author_name && (
                      <p className="mt-2 text-sm text-error-600">{errors.author_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[--text-primary]">
                      Category
                    </label>
                    <select
                      {...register('category_id')}
                      className="mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-[--text-primary]">
                        Content
                      </label>
                      <div className="flex bg-[--bg-tertiary] rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => setIsPreview(false)}
                          className={`flex items-center px-3 py-1.5 text-sm font-medium rounded ${
                            !isPreview
                              ? "bg-[--bg-primary] text-[--text-primary] shadow"
                              : "text-[--text-secondary] hover:text-[--text-primary]"
                          }`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsPreview(true)}
                          className={`flex items-center px-3 py-1.5 text-sm font-medium rounded ${
                            isPreview
                              ? "bg-[--bg-primary] text-[--text-primary] shadow"
                              : "text-[--text-secondary] hover:text-[--text-primary]"
                          }`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </button>
                      </div>
                    </div>
                    {isPreview ? (
                      <div className="mt-1 border border-[--border-secondary] rounded-md p-4 min-h-[400px] prose dark:prose-invert max-w-none">
                        {renderMarkdown(content || '')}
                      </div>
                    ) : (
                      <textarea
                        {...register('content')}
                        rows={15}
                        className="mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary] font-mono"
                      />
                    )}
                    {errors.content && (
                      <p className="mt-2 text-sm text-error-600">{errors.content.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[--text-primary]">
                      Status
                    </label>
                    <select
                      {...register('status')}
                      className="mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPost(null);
                        setIsEditing(false);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-[--border-secondary] rounded-md shadow-sm text-sm font-medium text-[--text-primary] bg-[--bg-primary] hover:bg-[--state-hover] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}